import React, { useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import HotelRoomMatchingService from '~/services/HotelRoomMatchingService';
import type { RoomScore } from '~/services/HotelRoomMatchingService';

import type { PropertyRoom, SourceRoom } from './types';

interface Props {
  sourceRoom: SourceRoom;
  candidateRoomNames: Array<PropertyRoom>;
  onQuickMap: (sourceRoom: SourceRoom, targetPropertyRoomName: string) => void;
  isQuickMapping?: boolean;
  onUpdateMLMatchScore: (sourceRoom: SourceRoom, bestScore: number) => void;
  appliedButNotSavedRooms?: Array<SourceRoom>;
}

export default function RoomMatchingSuggestions({
  sourceRoom,
  candidateRoomNames,
  onQuickMap,
  isQuickMapping,
  onUpdateMLMatchScore,
  appliedButNotSavedRooms = [],
}: Props) {
  const [allSuggestions, setAllSuggestions] = useState<Array<RoomScore>>([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCompletedFirstLoad, setHasCompletedFirstLoad] = useState(false);

  // Memoize the candidate room names to prevent unnecessary re-renders
  const stableCandidateNames = useMemo(() => {
    return candidateRoomNames.map((room) => room.name);
  }, [candidateRoomNames]);

  // Memoize the source room name
  const stableSourceRoomName = useMemo(() => {
    return sourceRoom.name;
  }, [sourceRoom.name]);

  // Create a stable key for the entire request to prevent duplicate calls
  // Only include candidate names on first load, after that we filter locally
  const requestKey = useMemo(() => {
    if (stableCandidateNames.length === 0 || hasCompletedFirstLoad) {
      return null;
    }
    return `${stableSourceRoomName}-${stableCandidateNames.join(',')}`;
  }, [stableSourceRoomName, stableCandidateNames, hasCompletedFirstLoad]);

  useEffect(() => {
    (async () => {
      if (requestKey && stableCandidateNames.length > 0 && !hasCompletedFirstLoad) {
        setIsMatching(true);
        setError(null);
        try {
          const response = await HotelRoomMatchingService.rankRoomMatchCandidates(
            stableSourceRoomName,
            stableCandidateNames,
          );
          setAllSuggestions(response.ranking);
          setConfidenceThreshold(response.confidenceThreshold);
          setHasCompletedFirstLoad(true);

          // Update the parent with the best ML match score
          if (response.ranking.length > 0) {
            onUpdateMLMatchScore(sourceRoom, response.ranking[0].score);
          }
        } catch (error) {
          console.error('Failed to get room matching suggestions:', error);
          setError('Failed to load room matching suggestions');
        } finally {
          setIsMatching(false);
        }
      }
    })();
  }, [requestKey, stableSourceRoomName, stableCandidateNames, sourceRoom, onUpdateMLMatchScore, hasCompletedFirstLoad]);

  // Filter suggestions based on available candidate rooms (don't recalculate ML scores)
  const suggestions = useMemo(() => {
    if (!hasCompletedFirstLoad) {
      return allSuggestions;
    }

    // Filter out suggestions for rooms that are no longer candidates
    return allSuggestions.filter((suggestion) => stableCandidateNames.includes(suggestion.roomName));
  }, [allSuggestions, stableCandidateNames, hasCompletedFirstLoad]);

  const renderSuggestion = (suggestion: RoomScore) => {
    const confidencePercent = (suggestion.score * 100).toFixed(1);
    const isHighConfidence = suggestion.score > confidenceThreshold;

    return (
      <Paper
        key={suggestion.roomName}
        variant="outlined"
        sx={{
          p: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderColor: isHighConfidence ? 'success.light' : 'warning.light',
          bgcolor: 'transparent',
        }}
      >
        <Typography variant="body2" sx={{ mr: 1, flex: 1 }}>
          {suggestion.roomName}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={`${confidencePercent}% match`} color={isHighConfidence ? 'success' : 'warning'} size="small" />
          <Chip
            label="Map"
            clickable
            onClick={() => onQuickMap(sourceRoom, suggestion.roomName)}
            color="primary"
            size="small"
            disabled={isQuickMapping}
            aria-label={`Map ${sourceRoom.name} to ${suggestion.roomName}`}
            role="button"
            tabIndex={0}
            sx={{
              cursor: isQuickMapping ? 'not-allowed' : 'pointer',
              opacity: isQuickMapping ? 0.5 : 1,
            }}
          />
        </Stack>
      </Paper>
    );
  };

  const topSuggestions = suggestions.slice(0, 2);
  const remainingSuggestions = suggestions.slice(2);

  return (
    <Paper sx={{ mt: 2, p: 1 }}>
      {(() => {
        if (isMatching && !error) {
          return (
            <Stack direction="row" gap={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">Analyzing room matches...</Typography>
            </Stack>
          );
        }

        if (error) {
          return (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          );
        }

        if (suggestions.length === 0) {
          return <Typography variant="body2">No suggestions available</Typography>;
        }

        return (
          <Stack spacing={0.5}>
            <Typography>
              <strong>AI suggestions:</strong>
            </Typography>
            {topSuggestions.map(renderSuggestion)}
            {remainingSuggestions.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Show more ({remainingSuggestions.length})</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0.5 }}>
                  <Stack spacing={0.5}>{remainingSuggestions.map(renderSuggestion)}</Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        );
      })()}
    </Paper>
  );
}
